import { useCallback, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'

// AI-RISK-REPORT-1 — one read-only screen of the AI features that need an owner
// decision, with the reason for each. Computed server-side; nothing is stored
// or sent. The CSV is built here from the loaded rows.

interface ReportItem {
  key: string
  name: string
  risk: string | null
  owner: string | null
  open_actions: number
  overdue_actions: number
  reasons: string[]
}

interface RiskReport {
  generated_at: string
  summary: { with_reasons: number; high_risk_no_action: number; action_overdue: number }
  items: ReportItem[]
  unavailable?: boolean
}

const REASON_LABELS: Record<string, string> = {
  risk_not_set: 'Risk not set',
  owner_missing: 'Owner missing',
  review_overdue: 'Review overdue',
  high_risk_no_action: 'High risk, no action recorded',
  action_overdue: 'Action overdue',
}

const CSV_HEADER = ['feature key', 'feature', 'risk', 'owner', 'open actions', 'overdue actions', 'reasons']

function producedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const text = d.toLocaleString('en-US', {
    timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
  return `${text} Central`
}

// Spreadsheet formula safety: a field starting with = + - @ gets a leading '.
function csvField(value: unknown): string {
  let s = value === null || value === undefined ? '' : String(value)
  if (/^[=+\-@]/.test(s)) s = `'${s}`
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function toCsv(items: ReportItem[]): string {
  const rows = items.map(i => [
    i.key, i.name, i.risk, i.owner, i.open_actions, i.overdue_actions,
    i.reasons.map(r => REASON_LABELS[r] ?? r).join('; '),
  ])
  return [CSV_HEADER, ...rows].map(r => r.map(csvField).join(',')).join('\r\n') + '\r\n'
}

function download(items: ReportItem[]) {
  const blob = new Blob([toCsv(items)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `ai-risk-report-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function AiRiskReportSection() {
  const { data, loading, error, execute } = useApi<RiskReport>()
  const load = useCallback(
    () => execute(() => apiClient.get<RiskReport>('/v1/admin/ai-risk-report').then(res => res.data)),
    [execute],
  )
  useEffect(() => { load() }, [load])

  const items = data?.items ?? []
  const summary = data?.summary

  return (
    <section data-testid="ai-risk-report-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Risk report</h2>
        {data && !data.unavailable && items.length > 0 && (
          <Button type="button" size="sm" data-testid="rr-csv" onClick={() => download(items)}>Download CSV</Button>
        )}
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && !data && <p className="text-sm text-gray-500">Loading…</p>}

      {data?.unavailable && (
        <p data-testid="rr-unavailable" className="text-sm text-red-600">The risk report could not be built.</p>
      )}

      {data && !data.unavailable && summary && (
        <>
          <p data-testid="rr-generated" className="text-xs text-gray-500">Produced {producedAt(data.generated_at)}</p>
          <p data-testid="rr-summary" className="text-sm text-gray-700">
            {summary.with_reasons} features need attention - {summary.high_risk_no_action} High risk with no action
            - {summary.action_overdue} actions overdue
          </p>
          {items.length === 0 ? (
            <p data-testid="rr-empty" className="text-sm text-gray-500">Nothing needs attention.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th>Feature</th><th>Risk</th><th>Owner</th><th>Open actions</th><th>Why</th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.key} data-testid={`rr-row-${i.key}`} className="border-t align-top">
                    <td className="py-2 pr-2">{i.name}</td>
                    <td className="py-2 pr-2">{i.risk ?? 'Not set'}</td>
                    <td className="py-2 pr-2">{i.owner ?? 'No owner'}</td>
                    <td className="py-2 pr-2">{i.open_actions}</td>
                    <td className="py-2">
                      {i.reasons.map(r => (
                        <span key={r}
                          className="mr-1 inline-flex rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          {REASON_LABELS[r] ?? r}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  )
}